import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  getRecord,
  getRecordTypeSchema,
  getRecordWithLinks,
  listAllRecordsWithLinks,
  transitionRecord,
  type RecordRow,
  type RecordsWithLinks,
  type RecordTypeSchema,
} from '../lib/api';
import { useRecordDetail } from '../lib/useRecordDetail';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { fmtMoney, humanizeValue } from '../lib/fieldDisplay';
import { AA, LINK, otherEnd, seasonEpisode, stepsFor } from '../lib/auditionSpread';
import {
  blockSpan,
  clauses,
  documentRounds,
  isSettled,
  NO_AMOUNT_RECORDED,
  orderShootDays,
  outstandingOn,
  rateLine,
  rowAmount,
  splitMoney,
} from '../lib/farSide';
import { ageLabel, ageInDays } from '../lib/aging';
import { openingPosition } from '../lib/stepRail';
import './spread.css';
import './farSide.css';

// A5 — THE ENGAGEMENT, AS A SPREAD. THE FAR SIDE OF THE SEAM.
//
// ===================================================================
// WHY THIS SCREEN EXISTS AT ALL
// ===================================================================
// Until now an engagement was reachable only through the schema-driven record
// editor: the FALLBACK door used as the front one. That is the exact defect
// A4 existed to fix for auditions, left standing one record over —
// RoleSurface's seam row called openRecordEditor, and this screen is what it
// calls instead.
//
// ===================================================================
// THE DESIGN LAW: BEFORE THE SEAM WE RENDER PROGRESS, AFTER IT OBLIGATIONS
// ===================================================================
// A4's spread is built for uncertainty — a stepper, a trail, a pipeline,
// instruments for measuring progress against an unknown end, because before a
// booking the question is *did we hear back*.
//
// After the seam nothing is uncertain any more. Days are real, amounts are
// owed, paperwork is either signed or it is not. Every region below is an
// OBLIGATION WITH A DATE AND A STATE, and the rule that follows from that is
// the one this screen is graded on:
//
//   LOUD WHEN LATE. QUIET WHEN DONE.
//
// A surface that renders an overdue payment with the same weight as a
// received one has failed at the only thing it was built for.
//
// ===================================================================
// THE FOUR REGIONS, AND WHAT EACH ANSWERS
// ===================================================================
//   the deal        what was agreed, and where it has got to
//   the days        what is coming — SOONEST FIRST, the opposite of an
//                   audition list, because a shoot day has not happened yet
//   the money       what arrived, what is late, what is not yet due
//   the paperwork   whether the contract is actually signed
//
// ===================================================================
// THE SEAM IS WALKABLE IN BOTH DIRECTIONS OR IT IS NOT DRAWN HONESTLY
// ===================================================================
// A4 gave the audition no way forward to what it became. The hero here
// carries a link BACK to the audition this came out of, when there was one.
// `engagement_audition` is a LOOKUP and OPTIONAL BY DESIGN — a returning
// guest star offered a part with no read at all is the NORMAL case, not an
// edge one (the pack's own header says so). So when the link is absent this
// screen renders nothing and implies nothing. It does not say "no audition",
// which would read as a missing record rather than a different route in.
//
// ===================================================================
// NO PACK VOCABULARY LIVES HERE
// ===================================================================
// The stepper is `stepsFor(schema.lifecycle, state)` — the SAME generic
// function A4 wrote, unchanged. On aa_engagement its one-hop rule yields
// `offered → accepted → contracted → shooting → wrapped` and drops
// `cancelled`. Not one state name appears in this file. Likewise "settled"
// money is derived from the transition table (farSide.ts's settledStates),
// never from a list of names that a future additive migration would silently
// falsify.

interface Props {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenRole: (id: string) => void;
  onOpenProject: (id: string) => void;
  onOpenAudition: (id: string) => void;
  onOpenDay: (id: string) => void;
}

interface Around {
  loaded: boolean;
  role: RecordRow | null;
  project: RecordRow | null;
  performer: RecordRow | null;
  audition: RecordRow | null;
  days: RecordRow[];
  payments: RecordRow[];
  documents: RecordRow[];
  paymentSchema: RecordTypeSchema | null;
  documentSchema: RecordTypeSchema | null;
}

const EMPTY: Around = {
  loaded: false,
  role: null,
  project: null,
  performer: null,
  audition: null,
  days: [],
  payments: [],
  documents: [],
  paymentSchema: null,
  documentSchema: null,
};

/** A record we may not read is a blank on this screen, not an error — but
 * ONLY a refusal is a blank. A network failure rethrows so the whole read
 * reports itself (AuditionSpread's own readOrNull rule). */
async function readOrNull(tenantId: string, id: string | null): Promise<RecordRow | null> {
  if (!id) return null;
  try {
    return await getRecord(tenantId, id);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
    throw err;
  }
}

/** A date, short. Local — a `date` field is a calendar day and rendering it
 * through UTC shows the day before for half of every day in Toronto. */
function when(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** A datetime, as a time of day. */
function at(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

type Tab = 'days' | 'money' | 'paperwork';

export function EngagementSurface(props: Props) {
  const { tenantId, id } = props;
  // useRecordDetail already fetches the record WITH its links — core's
  // listLinks matches src_id OR dst_id, so this one set has every end and a
  // second round trip for them would be pure latency.
  const { record, schema, links, error, reload } = useRecordDetail(tenantId, AA.ENGAGEMENT, id);
  const recordsVersion = useRecordsVersion();
  const [around, setAround] = useState<Around>(EMPTY);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('days');
  // B19 §2 — the read's B15 receipt shape, brought across: every stage move
  // leaves a word, `wrapped`/`cancelled` ask first (they carry weight
  // downstream — the credit, the end of the job), and Undo is offered only
  // when the fetched schema's own transition table already carries the move
  // back. Mirrored from AuditionSpread's `doMove`/`stageReceipt`/`confirmStage`
  // rather than shared: that screen's version is fused to audition-only
  // shapes (`passedOutcome`, the `bringBackTarget` archive revival) that do
  // not apply here, and a shared extraction would have reached past this brief.
  const [confirmStage, setConfirmStage] = useState<string | null>(null);
  const [stageReceipt, setStageReceipt] = useState<{
    text: string;
    from: string;
    to: string;
    canUndo: boolean;
  } | null>(null);
  const stepsRef = useRef<HTMLDivElement | null>(null);

  // ⚑ THE STEP YOU ARE ON IS ON THE SCREEN WHEN THE SCREEN OPENS.
  //
  // The stepper is one scrolling line now (see spread.css — a sequence that
  // wraps stops being a sequence), and a line that scrolls opens at its
  // START. On a phone that put SHOOTING half off the right edge: the state
  // the booking is actually in, clipped, on the screen whose whole job is to
  // say where the booking has got to.
  //
  // scrollLeft is SET, not animated. This is the screen's opening position,
  // not a transition — an arrival animation would be motion that explains
  // nothing, and it would run under `prefers-reduced-motion` too.
  useEffect(() => {
    const rail = stepsRef.current;
    if (!rail) return;
    // Lands on a step boundary, never inside a word (lib/stepRail.ts — the
    // first cut opened reading "FFERED" on a phone).
    const cells = Array.from(rail.querySelectorAll<HTMLElement>(':scope > .sp__step-cell'));
    const currentIndex = cells.findIndex((c) => c.querySelector('.sp__step--current'));
    const end = rail.querySelector<HTMLElement>(':scope > .sp__steps-end');
    if (currentIndex < 0) return;
    if (end) end.style.width = '0px';
    const railLeft = rail.getBoundingClientRect().left;
    const scrolled = rail.scrollLeft;
    const { scrollLeft, endPad } = openingPosition(
      cells.map((c) => {
        const r = c.getBoundingClientRect();
        return { left: r.left - railLeft + scrolled, width: r.width };
      }),
      currentIndex,
      rail.clientWidth
    );
    // The spacer makes the boundary reachable (lib/stepRail.ts — the browser clamps a scroll past
    // the content's end, which is how the rail opened reading "ED" on 2026-09-29).
    if (end) end.style.width = `${endPad}px`;
    rail.scrollLeft = scrollLeft;
  }, [record?.lifecycle_state, schema?.lifecycle]);

  const roleId = otherEnd(links, id, LINK.ENGAGEMENT_ROLE);
  const performerId = otherEnd(links, id, LINK.ENGAGEMENT_SUBJECT);
  const auditionId = otherEnd(links, id, LINK.ENGAGEMENT_AUDITION);

  useEffect(() => {
    let cancelled = false;
    if (!record) return;
    // A4's DEFECT 2, and the reason it is spelled out here rather than
    // trusted to habit: three detail screens claimed absence while their
    // second round trip was still in flight ("Nobody has read for this part
    // yet"). It self-heals in a blink on a desk, which is exactly why nothing
    // caught it, and a parent on a slow phone reads a sentence that is false.
    // `loaded` goes false on every re-read and no region may claim an absence
    // until it is true.
    setAround((prev) => ({ ...prev, loaded: false }));

    async function load() {
      try {
        const [role, performer, audition, allDays, allPayments, allDocs, paymentSchema, documentSchema] =
          await Promise.all([
            readOrNull(tenantId, roleId),
            readOrNull(tenantId, performerId),
            readOrNull(tenantId, auditionId),
            listAllRecordsWithLinks(tenantId, AA.SHOOT_DAY, { linkTypes: [LINK.SHOOT_DAY_ENGAGEMENT] }),
            listAllRecordsWithLinks(tenantId, AA.PAYMENT, { linkTypes: [LINK.PAYMENT_ENGAGEMENT] }),
            listAllRecordsWithLinks(tenantId, AA.DOCUMENT, { linkTypes: [LINK.DOCUMENT_ENGAGEMENT] }),
            getRecordTypeSchema(tenantId, AA.PAYMENT).catch(() => null),
            getRecordTypeSchema(tenantId, AA.DOCUMENT).catch(() => null),
          ]);
        if (cancelled) return;

        /** The rows of `bag` whose `linkType` owner is THIS engagement.
         * Every far-side type reaches its engagement by exactly one hop, so
         * one helper covers all three. */
        const mine = (bag: RecordsWithLinks, linkType: string): RecordRow[] =>
          activeOnly(bag.records).filter(
            (r) => otherEnd(bag.linksByRecordId[r.id] ?? [], r.id, linkType) === id
          );

        const days = mine(allDays, LINK.SHOOT_DAY_ENGAGEMENT);
        const payments = mine(allPayments, LINK.PAYMENT_ENGAGEMENT);
        const documents = mine(allDocs, LINK.DOCUMENT_ENGAGEMENT);

        const roleLinks = role ? await getRecordWithLinks(tenantId, role.id).catch(() => null) : null;
        const project = await readOrNull(
          tenantId,
          role && roleLinks ? otherEnd(roleLinks.links, role.id, LINK.ROLE_PROJECT) : null
        );
        if (cancelled) return;

        setAround({
          loaded: true,
          role,
          project,
          performer,
          audition,
          days,
          payments,
          documents,
          paymentSchema,
          documentSchema,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, record?.id, roleId, performerId, auditionId, recordsVersion]);

  if (error) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Booking</div>
        <div class="sp__say">
          {error.headline}
          <br />
          <span class="sp__say-soft">{error.detail}</span>
        </div>
        {error.retryable ? (
          <button type="button" class="sp__retry" onClick={reload}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!record) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Booking</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const money = (n: number) => fmtMoney(n);
  const day = (v: unknown) => when(v);

  const steps = stepsFor(schema?.lifecycle, record.lifecycle_state);
  const legalNext = schema?.lifecycle?.transitions[record.lifecycle_state ?? ''] ?? [];
  const dayOrder = orderShootDays(around.days);
  const split = splitMoney(around.payments, around.paymentSchema?.lifecycle);
  const rounds = documentRounds(around.documents);
  const se = around.project ? seasonEpisode(record.fields) : null;
  const block = blockSpan(record.fields, day);
  const rate = rateLine(record.fields, money);

  // THE HERO LINE, BUILT AND JOINED — never prefixed. A4's defect 5 was an
  // orphan '·' opening a line whose first clause turned out to be absent.
  const heroLine = clauses([
    around.project?.display_name,
    se,
    block,
    rate,
  ]);

  // WHICH JOB, IN THE RECEIPT — THE PART, as the read's receipts say it
  // ("WREN MALLOY moved to Booked."). Atlas's walk 2026-09-26: naming the
  // PERFORMER here read "Mark Robin Walkabout cancelled?" — cancelling a
  // person. A job with no part recorded says 'This job'.
  const performerLabel = around.role?.display_name?.trim() || 'This job';

  async function doMove(to: string, receiptText?: string) {
    setRefusal(null);
    setStageReceipt(null);
    const from = record!.lifecycle_state;
    try {
      await transitionRecord(tenantId, id, to);
      // THE GENERATED EDGES, NOT A HAND-KEPT LIST — the same rule
      // AuditionSpread's own `doMove` follows.
      const canUndo = Boolean(from && schema?.lifecycle?.transitions[to]?.includes(from));
      // B14's reaction ticks the credit onto Your page only when the move
      // lands on `wrapped` AND a performer is actually attached — never
      // re-implemented here, only reported: `around.performer` is the same
      // subject link the reaction reads server-side.
      const text =
        receiptText ??
        `${performerLabel} moved to ${humanizeValue(to)}` +
          (to === 'wrapped' && around.performer ? ' — the credit is on Your page.' : '.');
      setStageReceipt({ text, from: from ?? '', to, canUndo });
      reload();
    } catch (err) {
      // THE GATE IS THE SERVER'S — the pack's transition table is the only
      // copy of the rule, and re-stating it client-side would create a second
      // one to drift. The refusal shows in the words the server used.
      setRefusal(err instanceof ApiError ? err.message : 'that move was refused');
    }
  }

  /** `wrapped` and `cancelled` carry weight downstream (the credit, the end
   * of the job) and ask first, one line, in place; every other move goes
   * straight through, exactly as it always has. */
  function handleStepTap(to: string) {
    if (to === 'wrapped' || to === 'cancelled') {
      setRefusal(null);
      setStageReceipt(null);
      setConfirmStage(to);
      return;
    }
    void doMove(to);
  }

  function confirmStageMove() {
    const to = confirmStage;
    if (!to) return;
    setConfirmStage(null);
    void doMove(to);
  }

  /** Moves back to the state the receipt's move came FROM — the same act
   * AuditionSpread's own `undoStageMove` performs. */
  function undoStageMove() {
    if (!stageReceipt || !stageReceipt.canUndo) return;
    const { from } = stageReceipt;
    setStageReceipt(null);
    void doMove(from);
  }

  const outstandingTotal = split.aged.total;
  const contractRef =
    typeof record.fields.contract_ref === 'string' && record.fields.contract_ref.trim()
      ? record.fields.contract_ref.trim()
      : null;

  // ⚑ OUTSTANDING IS A FACT. LATE IS THE ALARM.
  //
  // The outstanding figure used to be set in the warning colour outright, so
  // a booking whose only payment fell due this morning shouted in amber at a
  // parent who owed nobody anything. Money not yet paid is the ordinary state
  // of a booking; money that is PAST its date is the thing worth a phone call.
  //
  // So the total is set in ink with tabular figures, and the severity colour
  // appears only on a line that carries the WORD — the same rule the row list
  // below already keeps (`fs__row--late` pairs its stripe with an age), and
  // the same one HouseholdMoney's chase now keeps beside it. Never colour
  // alone, on any of the three.
  const lateTotal = around.payments.reduce((n, p) => {
    if (isSettled(p, around.paymentSchema?.lifecycle)) return n;
    const age = ageInDays(p.fields.expected_on);
    return age !== null && age > 0 ? n + outstandingOn(p) : n;
  }, 0);
  const rail: { id: Tab; label: string; n: number }[] = [
    { id: 'days', label: 'Shoot days', n: around.days.length },
    { id: 'money', label: 'Money', n: around.payments.length },
    { id: 'paperwork', label: 'Paperwork', n: around.documents.length },
  ];

  return (
    <div class="sp">
      <div class="sp__crumb">
        {around.project ? (
          <>
            <button type="button" onClick={() => props.onOpenProject(around.project!.id)}>
              {around.project.display_name}
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        ) : null}
        {around.role ? (
          <>
            <button type="button" onClick={() => props.onOpenRole(around.role!.id)}>
              {around.role.display_name}
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        ) : (
          <>
            <button type="button" onClick={props.onBack}>
              Back
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        )}
        {/* The crumb says where the record actually stands — an offer that is
            merely offered must not read "booked" in the one label that frames
            the whole screen. */}
        <span class="sp__crumb-cur">
          {record.lifecycle_state ? humanizeValue(record.lifecycle_state).toLowerCase() : 'this booking'}
        </span>
      </div>

      {failure ? <div class="sp__say sp__say-soft">{failure.headline}</div> : null}

      <div class="sp__grid">
        {/* ================= THE DEAL ================= */}
        <div class="sp__hero">
          <div class="sp__hero-top">
            <div>
              <div class="sp__label">
                {/* THE PERFORMER IS NAMED HERE and nowhere else on the screen.
                    A booking belongs to one child; the role may span two, but
                    this record never does. */}
                {around.performer ? <span>{around.performer.display_name}</span> : null}
                <span class="fs__state">
                  {record.lifecycle_state ? humanizeValue(record.lifecycle_state) : 'booked'}
                </span>
              </div>
              {/* THE TITLE IS THE ROLE, NOT THE RECORD'S display_name.
                  aa_engagement's name template is `Season {season} — {episode_count} ep`,
                  which is a correct record name and an unreadable headline —
                  A4's defect 4, the same trap one type over. */}
              <div class="sp__title">{around.role?.display_name ?? 'This booking'}</div>
              {heroLine ? <div class="sp__sub">{heroLine}</div> : null}
            </div>
            <button type="button" class="sp__edit" onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.ENGAGEMENT, id })}>
              Edit
            </button>
          </div>

          {/* THE STEPPER — derived, no state name in this file. No per-gate
              dates: they are retained nowhere the browser can reach, and
              reading updated_at as "in this state since" is a number any
              field edit silently resets.

              ⚑ THE SAME MARKUP THE AUDITION'S STEPPER USES — `.sp__step-cell`
              wrapping a dot and a label, with the connector drawn after each
              cell but the last. This screen had grown its own shape (a bare
              `.sp__step-bar` BEFORE each label and no dot), and on a phone the
              five states broke into two ragged lines opening with a bar
              pointing at nothing. Two steppers is one too many; there is now
              one, and spread.css keeps it on a single scrolling line. */}
          {/* ⚑ AND THE STRIP IS THE CONTROL HERE TOO (Day 2 walk, the last
              finding). The audition's stepper MOVES the record when you tap a
              stage; this one looked identical and did nothing, and the move
              lived in a row of buttons underneath. One of the two had to be
              wrong, and it was this one: the strip is where a person's hand
              goes, the audition already taught them that, and a control that
              looks like the one next door and behaves differently is worse
              than either behaviour on its own.

              The rule is the audition's, unchanged: a stage is tappable only
              when the pack's own transition table says the move is legal from
              where the record actually is. Nothing here knows a state's name. */}
          <div class="sp__steps" ref={stepsRef}>
            {steps.map((s) => {
              const can = legalNext.includes(s.state);
              return (
                <div class="sp__step-cell" key={s.state}>
                  <button
                    type="button"
                    class={`sp__step${s.reached ? ' sp__step--reached' : ''}${s.current ? ' sp__step--current' : ''}${s.offSpine ? ' sp__step--off' : ''}`}
                    disabled={!can}
                    onClick={() => handleStepTap(s.state)}
                  >
                    <span class="sp__dot" />
                    <span class="sp__step-tx">{s.label}</span>
                  </button>
                </div>
              );
            })}
            <span class="sp__steps-end" aria-hidden="true" />
          </div>

          {/* ⚠ AND THE BUTTONS STAY FOR WHAT THE STRIP DOES NOT CARRY. A
              booking can be CANCELLED, which is off the spine and therefore
              off the strip — removing this row to match the audition exactly
              would have taken the only door to it. Only the moves the strip
              cannot offer are drawn here; a state that is now one tap away on
              the strip is not also a button, because two controls for one act
              is the disagreement this finding is about. */}
          {legalNext.filter((to) => !steps.some((s) => s.state === to)).length > 0 ? (
            <div class="sp__acts">
              {legalNext
                .filter((to) => !steps.some((s) => s.state === to))
                .map((to) => (
                  <button key={to} type="button" class="sp__act" onClick={() => handleStepTap(to)}>
                    {humanizeValue(to)}
                  </button>
                ))}
            </div>
          ) : null}
          {/* B19 §2 — `wrapped`/`cancelled` ask first, one line, in place —
              the same shape AuditionSpread's `booked`/`passed` confirm takes. */}
          {confirmStage ? (
            <p class="sp__age" data-testid="stage-confirm">
              Mark {performerLabel} {confirmStage === 'wrapped' ? 'wrapped' : 'cancelled'}?{' '}
              <button
                type="button"
                class="sp__inline-act"
                data-testid="stage-confirm-yes"
                onClick={confirmStageMove}
              >
                Yes
              </button>{' '}
              <button
                type="button"
                class="sp__inline-act"
                data-testid="stage-confirm-no"
                onClick={() => setConfirmStage(null)}
              >
                Not yet
              </button>
            </p>
          ) : null}
          {/* THE RECEIPT, WITH A WAY BACK WHEN ONE EXISTS — never an
              acknowledgement, it names the job and the move that happened
              to it. */}
          {stageReceipt ? (
            <p class="sp__age" data-testid="stage-receipt">
              {stageReceipt.text}
              {stageReceipt.canUndo ? (
                <>
                  {' '}
                  <button type="button" class="sp__inline-act" data-testid="stage-undo" onClick={undoStageMove}>
                    Undo
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {refusal ? (
            <p class="sp__age sp__age--late" data-testid="stage-refusal">
              {refusal}
            </p>
          ) : null}

          {/* ===================================================================
              HOW THIS BOOKING CAME ABOUT — a labelled trail, not three lines.
              ===================================================================
              These were three loose sentences hanging under the stepper —
              "← came out of this read", "Offer pinned down Aug 9, 11:00 AM",
              "Contract ref HRP-2211" — in three registers, at the same weight,
              with nothing saying they were one set of facts. They are: each is
              a label and its value, which is what `.sp__trail` already is on
              every other spread in this pack.

              THE READ IS A DOOR AND THE OTHER TWO ARE NOT, and the trail says
              so — a button that goes somewhere, and flat rows that state a
              fact. Dressing a contract reference as clickable is a promise the
              screen cannot keep.

              The read line is rendered only when there WAS an audition; absent
              means "came as a direct offer", which is normal and is not a gap
              to announce (`engagement_audition` is optional by design). */}
          {around.audition || record.fields.offered_at || contractRef ? (
            <div class="sp__trail">
              {around.audition ? (
                <button
                  type="button"
                  class="sp__trail-row"
                  onClick={() => props.onOpenAudition(around.audition!.id)}
                >
                  <span class="sp__trail-k">Came out of</span>
                  <span class="sp__trail-when">this read ›</span>
                </button>
              ) : null}
              {/* offered_at — the pack's answer to where the offer lives
                  (15155d2f, closed in A4): the audition records that an offer
                  ARRIVED; this records when the thing offered was PINNED DOWN,
                  and carries the direct-offer case with no audition at all. */}
              {record.fields.offered_at ? (
                <div class="sp__trail-row sp__trail-row--flat">
                  <span class="sp__trail-k">Offer pinned down</span>
                  <span class="sp__trail-when">{at(record.fields.offered_at)}</span>
                </div>
              ) : null}
              {contractRef ? (
                <div class="sp__trail-row sp__trail-row--flat">
                  <span class="sp__trail-k">Contract ref</span>
                  <span class="sp__trail-when">{contractRef}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ================= THE ASIDE: what is owed ================= */}
        <div class="sp__aside">
          <div class="sp__sec">Money</div>
          {!around.loaded ? (
            <div class="sp__empty">Reading…</div>
          ) : around.payments.length === 0 ? (
            <div class="sp__empty">Nothing recorded against this booking yet.</div>
          ) : (
            <>
              {/* EXPECTED AGAINST RECEIVED, BOTH STATED — never one net
                  figure. A net hides which half is missing, and which half is
                  missing is the entire question. */}
              <div class="fs__money-pair">
                <div>
                  <div class="fs__money-k">Outstanding</div>
                  <div class={`fs__money-v${outstandingTotal > 0 ? ' fs__money-v--owed' : ''}`}>
                    {money(outstandingTotal)}
                  </div>
                  {/* The WORD is the carrier; the figure is only repeated
                      when part of the balance is late and part is not.
                      "$2,900.00 / $2,900.00 late" states the same number
                      twice on two lines, which reads as two obligations. */}
                  {lateTotal > 0 ? (
                    <div class="fs__late" data-testid="engagement-late">
                      {lateTotal >= outstandingTotal ? 'all of it late' : `${money(lateTotal)} late`}
                    </div>
                  ) : null}
                </div>
                <div>
                  <div class="fs__money-k">Received</div>
                  <div class="fs__money-v">
                    {money(split.settled.reduce((n, r) => n + (typeof r.fields.received_amount === 'number' ? r.fields.received_amount : 0), 0))}
                  </div>
                </div>
              </div>

              {split.notYetDue.rows.length > 0 ? (
                <div class="fs__notdue">
                  {money(split.notYetDue.total)} not yet due
                  {split.notYetDue.soonest ? `, from ${day(split.notYetDue.soonest)}` : ''}
                </div>
              ) : null}

              {split.aged.bands
                .filter((b) => b.count > 0)
                .map((b) => (
                  <div key={b.key} class="fs__band">
                    <span class="fs__band-dot" style={{ background: b.color }} />
                    <span class="fs__band-label">{b.label}</span>
                    <span class="fs__band-total">{money(b.total)}</span>
                  </div>
                ))}
              {split.aged.unknown.count > 0 ? (
                <div class="fs__band fs__band--unknown">
                  <span class="fs__band-dot" />
                  <span class="fs__band-label">No date recorded</span>
                  <span class="fs__band-total">{money(split.aged.unknown.total)}</span>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* ================= THE BODY: days / money / paperwork ================= */}
        <div class="sp__body">
          <div class="sp__tabs">
            {rail.map((r) => (
              <button
                key={r.id}
                type="button"
                class={`sp__tab${tab === r.id ? ' sp__tab--on' : ''}`}
                onClick={() => setTab(r.id)}
              >
                {r.label} <span class={`sp__pill-n${r.n === 0 ? ' sp__pill--zero' : ''}`}>{r.n}</span>
              </button>
            ))}
          </div>

          {tab === 'days' ? (
            <>
            {!around.loaded ? (
              <div class="sp__empty">Reading…</div>
            ) : around.days.length === 0 ? (
              <div class="sp__empty">No call sheets yet.</div>
            ) : (
              <div class="sp__list">
                {/* SOONEST FIRST — a shoot day is a thing that is COMING.
                    An audition list is newest-first because a read is a thing
                    that HAPPENED. Getting this backwards puts last month's
                    wrap above tomorrow's 5:40am pickup. */}
                {dayOrder.upcoming.map((d, i) => (
                  <DayRow key={d.id} row={d} next={i === 0} onOpen={() => props.onOpenDay(d.id)} />
                ))}
                {dayOrder.past.length > 0 ? <div class="fs__divider">already shot</div> : null}
                {dayOrder.past.map((d) => (
                  <DayRow key={d.id} row={d} onOpen={() => props.onOpenDay(d.id)} />
                ))}
                {dayOrder.undated.length > 0 ? <div class="fs__divider">no date yet</div> : null}
                {dayOrder.undated.map((d) => (
                  <DayRow key={d.id} row={d} onOpen={() => props.onOpenDay(d.id)} />
                ))}
              </div>
            )}
            {/* The door in, pre-pointed at THIS booking — the same
                linked-create the role and tape spreads carry, so a new day
                never lands attached to nothing. */}
            <div class="sp__acts">
              <button
                type="button"
                class="sp__act"
                onClick={() =>
                  openRecordEditor({
                    mode: 'create',
                    typeKey: AA.SHOOT_DAY,
                    prefillRels: { [LINK.SHOOT_DAY_ENGAGEMENT]: [id] },
                  })
                }
              >
                + A call sheet arrived
              </button>
            </div>
            </>
          ) : null}

          {tab === 'money' ? (
            <>
            {!around.loaded ? (
              <div class="sp__empty">Reading…</div>
            ) : around.payments.length === 0 ? (
              <div class="sp__empty">Nothing paid or owing on this booking yet.</div>
            ) : (
              <div class="sp__list">
                {around.payments.map((p) => {
                  const settled = isSettled(p, around.paymentSchema?.lifecycle);
                  const age = ageInDays(p.fields.expected_on);
                  const late = !settled && age !== null && age > 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      class={`sp__row${late ? ' fs__row--late' : ''}${settled ? ' fs__row--done' : ''}`}
                      onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.PAYMENT, id: p.id })}
                    >
                      <span class="sp__row-top">
                        <span class="sp__row-name">
                          {typeof p.fields.kind === 'string' ? humanizeValue(p.fields.kind) : 'Payment'}
                        </span>
                        <span class="sp__row-when">
                          {/* NULL IS NOT ZERO — see `rowAmount` in farSide.ts. */}
                          {rowAmount(p) === null ? (
                            <span class="fs__absent">{NO_AMOUNT_RECORDED}</span>
                          ) : (
                            money(rowAmount(p)!)
                          )}
                        </span>
                      </span>
                      <span class="sp__row-sub">
                        {clauses([
                          p.lifecycle_state ? humanizeValue(p.lifecycle_state) : null,
                          // LOUD WHEN LATE, QUIET WHEN DONE. A settled row
                          // carries no age at all — "received, 41 days" reads
                          // as a complaint about money that already arrived.
                          settled ? null : ageLabel(age),
                        ])}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {/* Pre-pointed at this booking AND its performer, the same
                two-rel prefill RoleSurface's "+ Log the booking" carries. */}
            <div class="sp__acts">
              <button
                type="button"
                class="sp__act"
                onClick={() =>
                  openRecordEditor({
                    mode: 'create',
                    typeKey: AA.PAYMENT,
                    prefillRels: {
                      [LINK.PAYMENT_ENGAGEMENT]: [id],
                      ...(performerId ? { [LINK.PAYMENT_SUBJECT]: [performerId] } : {}),
                    },
                  })
                }
              >
                + Log a payment
              </button>
            </div>
            </>
          ) : null}

          {tab === 'paperwork' ? (
            <>
            {!around.loaded ? (
              <div class="sp__empty">Reading…</div>
            ) : rounds.length === 0 ? (
              <div class="sp__empty">No paperwork on file for this booking yet.</div>
            ) : (
              <div class="sp__trail">
                {/* ROUNDS ARE VERSIONS, NOT REPEATED STATES. The pack refuses
                    a redline → redline self-loop deliberately, so a surface
                    that rendered state changes would show ONE redline where
                    there were four. */}
                {rounds.map((r) => (
                  <button
                    key={r.record.id}
                    type="button"
                    class="sp__trail-row"
                    onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.DOCUMENT, id: r.record.id })}
                  >
                    <span class="sp__row-name">{r.label}</span>
                    <span class="sp__row-sub">
                      {clauses([
                        typeof r.record.fields.title === 'string' ? r.record.fields.title : null,
                        typeof r.record.fields.kind === 'string' ? humanizeValue(r.record.fields.kind) : null,
                        r.record.lifecycle_state ? humanizeValue(r.record.lifecycle_state) : null,
                      ])}
                    </span>
                    <span class="sp__trail-when">{day(r.record.fields.received_at) ?? ''}</span>
                  </button>
                ))}
              </div>
            )}
            <div class="sp__acts">
              <button
                type="button"
                class="sp__act"
                onClick={() =>
                  openRecordEditor({
                    mode: 'create',
                    typeKey: AA.DOCUMENT,
                    prefillRels: {
                      [LINK.DOCUMENT_ENGAGEMENT]: [id],
                      ...(performerId ? { [LINK.DOCUMENT_SUBJECT]: [performerId] } : {}),
                    },
                  })
                }
              >
                + File paperwork
              </button>
            </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** One shoot day in a list. The NEXT one is raised — it is the only row on
 * this screen the parent is going to act on before tomorrow morning. */
function DayRow(props: { row: RecordRow; next?: boolean; onOpen: () => void }) {
  const { row } = props;
  const pickup = at(row.fields.pickup_at);
  const date = when(row.fields.shoot_date);
  return (
    <button type="button" class={`sp__row${props.next ? ' fs__row--next' : ''}`} onClick={props.onOpen}>
      <span class="sp__row-top">
        <span class="sp__row-name">
          {typeof row.fields.day_label === 'string' && row.fields.day_label.trim()
            ? row.fields.day_label
            : (date ?? 'Shoot day')}
        </span>
        <span class="sp__row-when">{pickup ? `pickup ${pickup}` : ''}</span>
      </span>
      <span class="sp__row-sub">
        {clauses([
          typeof row.fields.day_label === 'string' && row.fields.day_label.trim() ? date : null,
          typeof row.fields.location === 'string' ? row.fields.location : null,
        ])}
      </span>
    </button>
  );
}
